import { useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SelectButton } from 'primereact/selectbutton';
import {
    ApiOperatorTaskType,
    type OperatorTaskDetailsResponse,
    type OperatorTaskOptionsResponse,
} from '@/core/generated/api';
import { formToPreview, operatorTaskToForm, reminderSettingsToForm } from '@/entities/OperatorTask/mappers';
import { createOperatorTaskSchema, type OperatorTaskFormValues } from '@/entities/OperatorTask/model/schema';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { EntityForm, EntityFormFields } from '@/shared/ui/EntityFormLayout/EntityFormLayout';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useCurrentTime } from '@/shared/hooks/useCurrentTime';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { calendarDateToDay, formatMoscowDateTime, getMoscowCalendarParts } from '@/shared/lib/dateTime';
import { useOperatorTaskSubmit } from '../model/useOperatorTaskSubmit';
import { useTaskReminderPreview } from '../model/useTaskReminderPreview';
import { TaskReminderFields } from './TaskReminderFields';
import { TaskChatSearchField } from './TaskChatSearchField';
import { TaskOrderSearchField } from './TaskOrderSearchField';
import { TaskAssigneeField } from './TaskAssigneeField';
import classes from './OperatorTaskForm.module.scss';
import type { OperatorTaskOrigin } from '@/entities/OperatorTask/model/types';
import { TaskRelatedLinks } from '@/entities/OperatorTask/ui/TaskRelatedLinks';
import { ApiOperatorTaskSource } from '@/core/generated/api';
import { OPERATOR_TASK_FORM_GROUP } from '../model/constants';

const { TextareaField, DateField, TimeField, SelectField } = createRHFFields<OperatorTaskFormValues>();

const taskTypes = [
    {
        id: ApiOperatorTaskType.OneTime,
        name: 'Разовая · в конкретное время',
    },
    {
        id: ApiOperatorTaskType.Deadline,
        name: 'Длительная · до дедлайна',
    },
];

const contextKinds = [
    {
        id: 'none',
        name: 'Без привязки',
    },
    {
        id: 'order',
        name: 'Заказ',
    },
    {
        id: 'chat',
        name: 'Чат',
    },
];

interface OperatorTaskFormProps {
    options: OperatorTaskOptionsResponse;
    task?: OperatorTaskDetailsResponse;
    clockOffset: number;
    onSaved: (task: OperatorTaskDetailsResponse) => void;
    onClose: () => void;
    onReload: () => void;
    onBusyChange: (busy: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    origin?: OperatorTaskOrigin;
    sourceUnavailable?: boolean;
}

/** Позволяет создать или изменить общую задачу */
export const OperatorTaskForm = (
    { options, task, clockOffset, onSaved, onClose, onReload, onBusyChange, onDirtyChange, origin, sourceUnavailable = false }: OperatorTaskFormProps
) => {
    const original = useRef(task).current;
    const schema = useMemo(() => createOperatorTaskSchema(options, () => Date.now() + clockOffset, original), [options, clockOffset, original]);

    const form = useUnsavedForm<OperatorTaskFormValues>({
        resolver: zodResolver(schema),
        defaultValues: operatorTaskToForm(options, original, origin),
        mode: 'onBlur',
        reValidateMode: 'onChange',
    }, true, OPERATOR_TASK_FORM_GROUP);

    const values = useWatch({ control: form.control });
    const now = useCurrentTime(clockOffset);
    const earliestDeadline = getMoscowCalendarParts(new Date(Math.floor(now / 60000) * 60000 + 60000).toISOString());
    const earliestDay = calendarDateToDay(earliestDeadline.date);

    const dueDay = values.dueDate instanceof Date
        ? calendarDateToDay(values.dueDate)
        : '';

    const minTime = dueDay === earliestDay
        ? earliestDeadline.time
        : undefined;

    const previousDueDay = useRef(dueDay);
    const contextKind = values.contextKind ?? 'order';
    const priorContextKind = useRef(contextKind);

    useEffect(() => {
        if (previousDueDay.current === dueDay) return;

        previousDueDay.current = dueDay;
        const time = form.getValues('dueTime');

        if (minTime && time && time < minTime) form.setValue('dueTime', '', {
            shouldDirty: true,
            shouldValidate: true
        });
    }, [dueDay, minTime, form]);

    useEffect(() => {
        if (priorContextKind.current === contextKind) return;

        priorContextKind.current = contextKind;

        form.setValue('related', null, {
            shouldDirty: true,
            shouldValidate: true
        });
    }, [contextKind, form]);

    const submit = useOperatorTaskSubmit(form, onSaved, original, origin);
    const parsed = schema.safeParse(values);

    const preview = useTaskReminderPreview(parsed.success
        ? formToPreview(parsed.data, original)
        : null);

    const busy = form.formState.isSubmitting;

    useEffect(() => {
        onBusyChange(busy);

        return () => onBusyChange(false);
    }, [busy, onBusyChange]);

    useEffect(() => {
        onDirtyChange?.(form.formState.isDirty);

        return () => onDirtyChange?.(false);
    }, [form.formState.isDirty, onDirtyChange]);

    return (
        <FormProvider {...form}>
            <EntityForm onSubmit={form.handleSubmit((submitted) => { if (!sourceUnavailable) return submit.onSubmit(submitted); })}>
                <p className={classes.hint}>Задача появится в общей очереди операторов</p>
                <TextareaField
                    name="text"
                    label="Название / описание"
                    placeholder="Что нужно сделать"
                    rows={3}
                    isRequired
                    disabled={busy}
                />
                <Controller
                    name="type"
                    control={form.control}
                    render={({ field }) => (
                        <div className={classes.typeField}>
                            <span id="task-type-label">Тип задачи *</span>
                            <SelectButton
                                value={field.value}
                                options={taskTypes}
                                optionLabel="name"
                                optionValue="id"
                                allowEmpty={false}
                                aria-labelledby="task-type-label"
                                disabled={busy}
                                onChange={(event) => {
                                    if (event.value !== ApiOperatorTaskType.OneTime && event.value !== ApiOperatorTaskType.Deadline) return;

                                    const defaults = event.value === ApiOperatorTaskType.OneTime
                                        ? options.oneTimeDefaults
                                        : options.deadlineDefaults;

                                    form.reset({
                                        ...form.getValues(),
                                        type: event.value,
                                        ...reminderSettingsToForm(defaults, getMoscowCalendarParts(options.serverTimeUtc ?? new Date().toISOString()).date),
                                    }, { keepDefaultValues: true });
                                }}
                            />
                        </div>
                    )}
                />
                <EntityFormFields>
                    <DateField
                        name="dueDate"
                        label={values.type === ApiOperatorTaskType.Deadline
                            ? 'Выполнить до'
                            : 'Дата выполнения'
                        }
                        isRequired
                        disabled={busy}
                        minDate={earliestDeadline.date}
                    />
                    <TimeField
                        name="dueTime"
                        label="Время"
                        placeholder="15:00"
                        isRequired
                        disabled={busy || Boolean(dueDay && dueDay < earliestDay)}
                        minTime={minTime}
                    />
                </EntityFormFields>
                <Controller
                    name="assignee"
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <TaskAssigneeField
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={busy}
                            error={fieldState.error?.message}
                        />
                    )}
                />
                {!origin &&
                    <SelectField
                        name="contextKind"
                        label="Тип связи"
                        options={contextKinds}
                        disabled={busy}
                    />
                }
                {!origin && contextKind !== 'none' &&
                    <Controller
                        name="related"
                        control={form.control}
                        render={({ field, fieldState }) => contextKind === 'chat'
                            ? (
                                <TaskChatSearchField
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    disabled={busy}
                                    error={fieldState.error?.message}
                                />
                            )
                            : (
                                <TaskOrderSearchField
                                    name={field.name}
                                    label="Связанный заказ или чат"
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={fieldState.error?.message}
                                    isRequired
                                    disabled={busy}
                                />
                            )}
                    />
                }
                {origin && (
                    <section className={classes.preview}>
                        <h3>Связанный заказ или чат</h3>
                        <TaskRelatedLinks context={origin.source === ApiOperatorTaskSource.Order
                            ? { order: origin.related }
                            : { chat: origin.related }
                        } />
                        <small>{origin.source === ApiOperatorTaskSource.Order
                            ? 'Связь подставлена из открытого заказа'
                            : 'Связь подставлена из открытого чата'}</small>
                    </section>
                )}
                {sourceUnavailable &&
                    <RequestError
                        message="Исходный заказ или чат изменился либо недоступен. Вернитесь к нему для сохранения или закройте форму"
                        compact
                    />
                }
                {original?.context?.contextVerificationUnavailable && (
                    <p className={classes.hint}>Проверка чата временно недоступна. Прежняя связь сохранится, если её не менять</p>
                )}
                {(original?.context?.order?.availability?.id === 'unavailable' || original?.context?.chat?.availability?.id === 'unavailable') && (
                    <p className={classes.hint}>Связанный объект недоступен. Прежняя связь сохранится, если её не менять</p>
                )}
                <TaskReminderFields
                    options={options}
                    originalIntervalHours={original?.reminders?.overdue?.intervalHours}
                />
                <section className={classes.preview}>
                    <h3>Ближайшие уведомления</h3>
                    {preview.status === RequestStatus.Loading &&
                        <RequestLoading message="Расчёт напоминаний..." />
                    }
                    {preview.status === RequestStatus.Error &&
                        <RequestError
                            message={preview.error ?? 'Не удалось рассчитать напоминания'}
                            onRetry={preview.retry}
                            compact
                        />
                    }
                    {preview.status === RequestStatus.Idle &&
                        <p>Заполните срок, связь и настройки, чтобы увидеть расписание</p>
                    }
                    {preview.data?.items?.map((moment) => (
                        <div
                            className={classes.previewRow}
                            key={moment.scheduledAtUtc}
                        >
                            <span>{formatMoscowDateTime(moment.scheduledAtUtc)}</span>
                            <small>{moment.reasons?.map((reason) => reason.name).join(', ')}</small>
                        </div>
                    ))}
                    {preview.status === RequestStatus.Success && !preview.data?.items?.length &&
                        <RequestEmpty message="Нет ближайших напоминаний" />
                    }
                    {preview.data?.hasMore &&
                        <small>Далее — по выбранному расписанию до выполнения задачи</small>
                    }
                </section>
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                }
                {submit.conflict && (
                    <div className={classes.conflict}>
                        <p>Задача изменена другим сотрудником. Ваши правки остались в форме. Перед продолжением загрузите актуальную задачу</p>
                        <ActionButton
                            label="Загрузить актуальную задачу"
                            outlined
                            onClick={() => confirmUnsavedChanges(form.formState.isDirty, () => {
                                form.reset();
                                onReload();
                            })}
                            type="button"
                        />
                    </div>
                )}
                <div className={classes.actions}>
                    <ActionButton
                        label="Отмена"
                        type="button"
                        outlined
                        disabled={busy}
                        onClick={() => confirmUnsavedChanges(form.formState.isDirty, () => {
                            form.reset();
                            onClose();
                        })}
                    />
                    <ActionButton
                        label={original
                            ? 'Сохранить'
                            : 'Создать задачу'
                        }
                        type="submit"
                        icon={original
                            ? 'pi pi-check'
                            : 'pi pi-plus'
                        }
                        permission={original
                            ? 'admin.operator-tasks.edit'
                            : 'admin.operator-tasks.create'
                        }
                        loading={busy}
                        disabled={busy || submit.conflict || sourceUnavailable || Boolean(original && !form.formState.isDirty)}
                    />
                </div>
            </EntityForm>
        </FormProvider>
    );
};
